// ---------------------------------------------------------------------------
// MKT-017 Phase 5 — White-Label Storefront CQRS Event Catalog (vocabulary)
// ---------------------------------------------------------------------------
// Story MKT-017: "White-label marketplace lets an agency sell its own
// solutions under its brand."
//
// Phase 1 (LANDED 2026-05-14) shipped the pure classifier
// `packages/marketing/src/compliance/mkt-017-white-label-storefront-classifier.ts`
// with closed verdict + listing-kind + blocker + billing-mode + currency +
// customisation-policy catalogs and 23-case proof.
//
// This file is the Phase 5 narrow follow-up: the closed CQRS event-catalog
// vocabulary the future Phase 3 submit + admin review + publish routes
// share. Per Governance Rule #7 ("Event Catalog"): every event type
// catalogued BEFORE Phase 3 runtime wiring so emitters import the constants
// instead of hand-rolling drift-prone strings.
//
// What this file is NOT:
// - It does not emit events — that's Phase 3 (route wiring).
// - It does not mount routes — Phase 3 is route-tier.
// - It does not change the Phase 1 classifier contract (additive only).
//
// What it IS:
// - The single source of truth for `agency.storefront_listing.*` event strings.
// - Typed payload contracts so emitters and DLQ consumers cannot drift.
// - Runtime narrowing guards so DLQ/audit-runner reject unknown event types.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of the 2 MKT-017 CQRS event types. Versioned with `@v1`
 * suffix per platform convention so future shape changes ship a `@v2`
 * alongside (never mutate in place).
 *
 * One business fact per event (Governance Rule #6):
 *  - `classified` — classifier or admin landed a verdict on a storefront listing.
 *  - `published`  — agency published a previously-classified listing to its storefront.
 */
export const STOREFRONT_LISTING_EVENT_TYPES = [
  "agency.storefront_listing.classified@v1",
  "agency.storefront_listing.published@v1",
] as const;

export type StorefrontListingEventType =
  (typeof STOREFRONT_LISTING_EVENT_TYPES)[number];

/**
 * Closed catalog of listing kinds — mirrors Phase 1
 * `STOREFRONT_LISTING_KINDS` verbatim so the DB tier does not invert
 * dependency direction onto the marketing package. Drift between this and
 * Phase 1 fails the proof test.
 */
export const STOREFRONT_LISTING_KIND_VOCAB = [
  "workflow_bundle",
  "template_bundle",
  "report_bundle",
  "service_offering",
  "mixed_pack",
] as const;

export type StorefrontListingKindVocab =
  (typeof STOREFRONT_LISTING_KIND_VOCAB)[number];

/**
 * Closed catalog of verdicts — mirrors Phase 1 `STOREFRONT_LISTING_VERDICTS`
 * verbatim. Used by `classified@v1` payload.
 */
export const STOREFRONT_LISTING_VERDICT_VOCAB = [
  "auto_approve",
  "needs_review",
  "rejected",
] as const;

export type StorefrontListingVerdictVocab =
  (typeof STOREFRONT_LISTING_VERDICT_VOCAB)[number];

/**
 * Closed catalog of decision sources. Identifies whether the verdict came
 * from the automated classifier or an admin operator.
 */
export const STOREFRONT_LISTING_DECISION_SOURCES = [
  "classifier",
  "admin_review",
] as const;

export type StorefrontListingDecisionSource =
  (typeof STOREFRONT_LISTING_DECISION_SOURCES)[number];

/**
 * Runtime narrowing guard. DLQ consumers + audit-runner use this to reject
 * unknown event types at the boundary instead of silently fanning them
 * downstream.
 */
export function isStorefrontListingEventType(
  value: unknown,
): value is StorefrontListingEventType {
  return (
    typeof value === "string" &&
    (STOREFRONT_LISTING_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isStorefrontListingKindVocab(
  value: unknown,
): value is StorefrontListingKindVocab {
  return (
    typeof value === "string" &&
    (STOREFRONT_LISTING_KIND_VOCAB as readonly string[]).includes(value)
  );
}

export function isStorefrontListingVerdictVocab(
  value: unknown,
): value is StorefrontListingVerdictVocab {
  return (
    typeof value === "string" &&
    (STOREFRONT_LISTING_VERDICT_VOCAB as readonly string[]).includes(value)
  );
}

export function isStorefrontListingDecisionSource(
  value: unknown,
): value is StorefrontListingDecisionSource {
  return (
    typeof value === "string" &&
    (STOREFRONT_LISTING_DECISION_SOURCES as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Per-event payload contracts
// ---------------------------------------------------------------------------

export interface StorefrontListingBaseEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** Agency selling under its own brand (FK to `organizations`). */
  agencyId: string;
  /** FK into the future `agency_white_label_storefront_listings` table. */
  listingId: string;
  /** Listing kind — one of `STOREFRONT_LISTING_KIND_VOCAB`. */
  kind: StorefrontListingKindVocab;
}

export interface StorefrontListingClassifiedPayload
  extends StorefrontListingBaseEventPayload {
  /** Final verdict — one of `STOREFRONT_LISTING_VERDICT_VOCAB`. */
  verdict: StorefrontListingVerdictVocab;
  /** Whether the verdict came from the automated classifier or an admin. */
  source: StorefrontListingDecisionSource;
  /**
   * Phase 1 blocker codes that drove the rejected/needs_review verdict.
   * Empty array on auto_approve.
   */
  blockers: readonly string[];
  /** Admin reviewer (set when `source === "admin_review"`). */
  reviewerUserId: string | null;
  /** ISO-8601 — when the classification was finalised. */
  classifiedAt: string;
}

export interface StorefrontListingPublishedPayload
  extends StorefrontListingBaseEventPayload {
  /** Operator who published (audit). */
  publisherUserId: string;
  /** ISO-8601 — when the listing was made visible on the storefront. */
  publishedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (route emitter, materialiser, DLQ, audit-runner) `switch (event.type)` and
 * pull the right payload without any-casting.
 */
export type StorefrontListingEvent =
  | {
      type: "agency.storefront_listing.classified@v1";
      payload: StorefrontListingClassifiedPayload;
    }
  | {
      type: "agency.storefront_listing.published@v1";
      payload: StorefrontListingPublishedPayload;
    };
